// Informe de entrenamiento como un único archivo HTML, sin recursos externos:
// se abre igual en el móvil sin conexión y se guarda en PDF desde el diálogo de
// imprimir del navegador. Por eso los iconos y las gráficas son SVG escritos a
// mano y la tipografía es la del sistema.
//
// El informe se imprime, así que no lleva el fondo oscuro de la app. Va sobre
// papel hueso con el azul de TRYAD como tinta, en un registro de plano técnico:
// rejilla milimetrada, titulares condensados en mayúsculas con sombra
// desplazada, metadatos en monoespaciada y tarjetas con corchetes de esquina.
// Vieja escuela y pantalla a la vez, que es lo que se pidió.

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function num(value) {
  return Number(value || 0).toLocaleString("es-ES");
}

function date(iso, opts = { day: "numeric", month: "long", year: "numeric" }) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", opts);
}

function shortDate(iso) {
  return date(iso, { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function signed(value, unit = "") {
  if (value === null || value === undefined) return "—";
  const rounded = Number(value.toFixed(2));
  return `${rounded > 0 ? "+" : ""}${num(rounded)}${unit}`;
}

function trendClass(value) {
  if (!value) return "flat";
  return value > 0 ? "up" : "down";
}

// ─── Iconos ───────────────────────────────────────────────────────────────────
//
// Trazo de 1,6 sobre una caja de 24: al reducirlos a 22 px en las cifras y a 18
// en las secciones siguen leyéndose, que es más de lo que aguanta un icono
// relleno impreso en gris.

const ICONS = {
  sets: '<path d="M3 10v4M6 7v10M18 7v10M21 10v4M6 12h12"/>',
  volume: '<path d="M12 3c3 4 5 6 5 9a5 5 0 0 1-10 0c0-3 2-5 5-9z"/>',
  days: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 11h18"/>',
  streak: '<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>',
  cycles: '<path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/>',
  exercises: '<path d="M3 20h18"/><path d="M6 20V10M11 20V4M16 20v-8"/>',
  goal: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.5"/>',
  measures: '<rect x="2" y="8" width="20" height="8" rx="2"/><path d="M7 8v3M12 8v4M17 8v3"/>',
  routine: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h3"/>',
  cycleList: '<path d="M4 6h16M4 12h16M4 18h10"/>',
  muscle: '<circle cx="12" cy="5.5" r="2.6"/><path d="M7 21v-6.5a5 5 0 0 1 10 0V21"/><path d="M9 14h6"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4"/><path d="M7 12h2M11 12h2M15 12h2M7 16h2M11 16h2"/>',
  diet: '<path d="M6 3v8a3 3 0 0 0 6 0V3"/><path d="M9 11v10"/><path d="M18 3c-1.7 1.4-2.5 3.3-2.5 5.5S16.3 12.6 18 14V3z"/><path d="M18 14v7"/>',
};

function icon(name, size = 22) {
  return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ""}</svg>`;
}

// ─── Gráficas ─────────────────────────────────────────────────────────────────

// Miniatura de la evolución de un ejercicio: importa la forma de la línea, no
// los valores, que ya están en la fila.
function sparkline(entries, width = 62, height = 20) {
  if (!entries || entries.length < 2) return "";

  const values = entries.map((e) => e.weight);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);

  const points = values
    .map((value, i) => `${(i * step).toFixed(1)},${(height - 2 - ((value - min) / span) * (height - 4)).toFixed(1)}`)
    .join(" ");

  const last = points.split(" ").pop().split(",");

  return `<svg class="spark" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none">
    <polyline points="${points}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>
    <circle cx="${last[0]}" cy="${last[1]}" r="1.8" fill="currentColor"/>
  </svg>`;
}

const DONUT_TONES = ["#0A85FF", "#0A4E8F", "#5CB0FF", "#123A5C", "#9CC9F5", "#B9BFC7"];

// Reparto de series por grupo muscular.
//
// La suma de los sectores es mayor que las series hechas, porque un ejercicio
// de "Pecho/Tríceps" cuenta en los dos grupos. Por eso el centro no dice
// "series" y hay una nota debajo: poner ahí un total que no cuadra con el de
// arriba haría dudar de todo el informe.
function donut(volumeByGroup, totalSets) {
  const ranked = Object.entries(volumeByGroup).sort((a, b) => b[1] - a[1]);
  if (ranked.length === 0) return "";

  const top = ranked.slice(0, 5);
  const rest = ranked.slice(5).reduce((a, [, count]) => a + count, 0);
  const slices = rest > 0 ? [...top, ["Otros", rest]] : top;
  const sum = slices.reduce((a, [, count]) => a + count, 0) || 1;

  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  const arcs = slices
    .map(([, count], i) => {
      const length = (count / sum) * circumference;
      const arc = `<circle cx="50" cy="50" r="${radius}" fill="none" stroke="${DONUT_TONES[i % DONUT_TONES.length]}"
        stroke-width="15" stroke-dasharray="${length.toFixed(2)} ${(circumference - length).toFixed(2)}"
        stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 50 50)"/>`;
      offset += length;
      return arc;
    })
    .join("");

  const legend = slices
    .map(
      ([group, count], i) => `<li>
        <span class="dot" style="background:${DONUT_TONES[i % DONUT_TONES.length]}"></span>
        <span class="legend-name">${esc(group)}</span>
        <span class="legend-value">${num(count)}</span>
      </li>`
    )
    .join("");

  return `<div class="donut">
    <div class="donut-chart">
      <svg viewBox="0 0 100 100" width="132" height="132">${arcs}</svg>
      <div class="donut-center">
        <strong>${slices.length}</strong>
        <span>GRUPOS</span>
      </div>
    </div>
    <div class="donut-side">
      <ul class="legend">${legend}</ul>
      <p class="note">El reparto suma ${num(sum)} sobre ${num(totalSets)} series: un ejercicio que trabaja dos grupos cuenta en los dos.</p>
    </div>
  </div>`;
}

// ─── Bloques ──────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");

function kpi(index, { name, value, unit, label, delta, hint }) {
  const trend =
    delta === null || delta === undefined
      ? hint
        ? `<p class="kpi-hint">${esc(hint)}</p>`
        : ""
      : `<p class="kpi-delta ${trendClass(delta)}">${delta > 0 ? "▲" : delta < 0 ? "▼" : "="} ${signed(delta, "%")}<em>vs ciclo anterior</em></p>`;

  return `<div class="kpi">
    <span class="kpi-index">${pad(index)}</span>
    <span class="kpi-icon">${icon(name)}</span>
    <p class="kpi-value">${num(value)}${unit ? `<em>${esc(unit)}</em>` : ""}</p>
    <p class="kpi-label">${esc(label)}</p>
    ${trend}
  </div>`;
}

function section(index, id, iconName, title, subtitle, body) {
  if (!body) return "";
  return `<section id="${id}">
    <div class="section-head">
      <span class="section-num">${pad(index)}</span>
      <span class="section-icon">${icon(iconName, 18)}</span>
      <h2>${esc(title)}</h2>
      ${subtitle ? `<p>${esc(subtitle)}</p>` : ""}
    </div>
    ${body}
  </section>`;
}

// El ciclo de la rutina como recorrido, que es como se entrena: un día detrás
// de otro hasta cerrar la vuelta y volver a empezar.
function flow(days) {
  if (days.length === 0) return "";

  const steps = days
    .map((day, i) => {
      const sets = day.exercises.reduce((a, ex) => a + (ex.sets || 0), 0);
      return `<div class="step">
        <span class="step-index">${i + 1}</span>
        <p class="step-name">${esc(day.short || day.name)}</p>
        <p class="step-detail">${day.exercises.length} ejercicios · ${sets} series</p>
      </div>`;
    })
    .join('<span class="step-arrow">→</span>');

  return `<div class="flow">${steps}<span class="step-arrow loop">↻</span>
    <div class="step step-loop"><p class="step-name">REPETIR</p><p class="step-detail">y subir el peso</p></div>
  </div>`;
}

function muscleBars(volumeByGroup) {
  const ranked = Object.entries(volumeByGroup).sort((a, b) => b[1] - a[1]);
  if (ranked.length === 0) return "";

  const max = ranked[0][1];
  const total = ranked.reduce((a, [, count]) => a + count, 0);

  const rows = ranked
    .map(([group, count]) => {
      // La barra se normaliza contra el grupo más entrenado, igual que el mapa
      // corporal de la app: lo que interesa ver es el desequilibrio.
      const width = Math.max(2, Math.round((count / max) * 100));
      return `<div class="bar-row">
        <span class="bar-label">${esc(group)}</span>
        <span class="bar-track"><span class="bar-fill" style="width:${width}%"></span></span>
        <span class="bar-value">${Math.round((count / total) * 100)}%</span>
      </div>`;
    })
    .join("");

  return `<div class="bars">${rows}</div>`;
}

function routineBlock(routine) {
  if (routine.days.length === 0) return "";

  const days = routine.days
    .map((day) => {
      const totalSets = day.exercises.reduce((a, ex) => a + (ex.sets || 0), 0);
      const rows = day.exercises
        .map(
          (ex) => `<tr>
            <td>${esc(ex.name)}</td>
            <td class="dim">${esc(ex.muscleGroup || "—")}</td>
            <td class="right nums">${esc(ex.sets)}</td>
            <td class="right nums">${esc(ex.reps || "—")}</td>
          </tr>`
        )
        .join("");

      return `<div class="card day">
        <div class="card-head">
          <h3>${esc(day.name)}</h3>
          <span class="pill">${day.exercises.length} ejercicios · ${totalSets} series</span>
        </div>
        ${
          rows
            ? `<table>
                <thead><tr><th>Ejercicio</th><th>Músculo</th><th class="right">Series</th><th class="right">Reps</th></tr></thead>
                <tbody>${rows}</tbody>
              </table>`
            : `<p class="empty">Sin ejercicios.</p>`
        }
      </div>`;
    })
    .join("");

  return `<div class="stack">${days}</div>`;
}

function exerciseTable(exercises) {
  if (exercises.length === 0) return "";

  const rows = exercises
    .map(
      (ex) => `<tr>
        <td><span class="ex-name">${esc(ex.name)}</span></td>
        <td class="spark-cell">${sparkline(ex.entries)}</td>
        <td class="right nums">${num(ex.first)}</td>
        <td class="right nums strong">${num(ex.last)}</td>
        <td class="right nums">${num(ex.best)}</td>
        <td class="right nums ${trendClass(ex.delta)}">${signed(ex.delta, " kg")}</td>
        <td class="right nums ${trendClass(ex.delta)}">${ex.percent === null ? "—" : signed(ex.percent, "%")}</td>
        <td class="right nums dim">${ex.sessions}</td>
      </tr>`
    )
    .join("");

  return `<div class="card"><table class="wide">
    <thead><tr>
      <th>Ejercicio</th>
      <th>Evolución</th>
      <th class="right">Inicio</th>
      <th class="right">Actual</th>
      <th class="right">Récord</th>
      <th class="right">Δ</th>
      <th class="right">%</th>
      <th class="right">Sesiones</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

// Un ciclo archivado, con lo que de verdad se levantó en cada serie.
function cycleBlock(cycle, { open = false } = {}) {
  const days = cycle.days
    .map((day) => {
      const rows = day.exercises
        .map((ex) => {
          const sets = ex.sets
            .map((s) => `<span class="set">${num(s.weight)}<em>kg</em> × ${num(s.reps)}</span>`)
            .join("");
          const volume = ex.sets.reduce((a, s) => a + (s.weight || 0) * (s.reps || 0), 0);
          return `<tr>
            <td>
              <span class="ex-name">${esc(ex.name)}</span>
              <span class="ex-group">${esc(ex.muscleGroup || "")}</span>
            </td>
            <td class="sets">${sets}</td>
            <td class="right nums dim">${num(Math.round(volume))} kg</td>
          </tr>`;
        })
        .join("");

      return `<div class="cycle-day">
        <div class="cycle-day-head">
          <h4>${esc(day.name)}</h4>
          <span class="pill">${day.exercises.length} ejercicios</span>
        </div>
        <table><tbody>${rows}</tbody></table>
      </div>`;
    })
    .join("");

  return `<div class="card cycle">
    <div class="card-head">
      <h3>Semana ${esc(cycle.week)}${open ? ' <span class="tag">en curso</span>' : ""}</h3>
      <span class="pill">${open ? "Sin cerrar" : `Terminado el ${date(cycle.completedAt, { day: "numeric", month: "short", year: "numeric" })}`}</span>
    </div>
    <div class="cycle-stats">
      <span><strong>${num(cycle.totalSets ?? cycle.sets)}</strong> series</span>
      <span><strong>${num(cycle.totalVolume ?? cycle.volume)}</strong> kg de volumen</span>
    </div>
    ${days || `<p class="empty">Ninguna serie registrada.</p>`}
  </div>`;
}

function measurementsBlock(measurements) {
  if (measurements.length === 0) return "";

  const rows = measurements
    .map(
      (m) => `<tr>
        <td>${esc(m.name)}${m.custom ? ' <span class="tag">propia</span>' : ""}</td>
        <td class="right nums">${num(m.first)} ${esc(m.unit)}</td>
        <td class="right nums strong">${num(m.last)} ${esc(m.unit)}</td>
        <td class="right nums ${trendClass(m.delta)}">${signed(m.delta, ` ${m.unit}`)}</td>
        <td class="right nums dim">${m.entries.length}</td>
        <td class="dim">${shortDate(m.firstDate)} → ${shortDate(m.lastDate)}</td>
      </tr>`
    )
    .join("");

  // El detalle de cada lectura va aparte: la tabla de arriba responde "cómo voy"
  // y esta, "qué apunté y cuándo".
  const detail = measurements
    .map((m) => {
      const points = m.entries
        .map((e) => `<span class="reading"><em>${shortDate(e.date)}</em> ${num(e.value)} ${esc(m.unit)}</span>`)
        .join("");
      return `<div class="metric">
        <h4>${esc(m.name)}</h4>
        <div class="readings">${points}</div>
      </div>`;
    })
    .join("");

  return `<div class="card"><table class="wide">
    <thead><tr>
      <th>Métrica</th>
      <th class="right">Inicio</th>
      <th class="right">Actual</th>
      <th class="right">Δ</th>
      <th class="right">Registros</th>
      <th>Periodo</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table></div>
  <div class="metrics">${detail}</div>`;
}

const WEEKDAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// La dieta se agrupa por día y no como la rejilla de siete columnas de la app:
// impresa, esa rejilla deja cada celda en una tira de dos centímetros donde no
// cabe "Burrito (fajita + alubias...)".
function dietBlock(diet) {
  if (!diet || diet.meals.length === 0) return "";

  const days = WEEKDAY_NAMES.map((name, i) => {
    const rows = diet.meals
      .map((meal) => {
        const text = diet.plan[meal.id]?.[i] || "";
        if (!text) return "";
        return `<tr>
          <th class="meal-name">${esc(meal.name)}</th>
          <td>${esc(text)}</td>
        </tr>`;
      })
      .filter(Boolean)
      .join("");

    if (!rows) return "";
    return `<div class="card diet-day">
      <div class="card-head"><h4>${name}</h4></div>
      <table><tbody>${rows}</tbody></table>
    </div>`;
  })
    .filter(Boolean)
    .join("");

  if (!days) return "";

  return `<div class="diet">${days}</div>
    ${diet.note ? `<p class="diet-note">${esc(diet.note)}</p>` : ""}`;
}

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Una tira de días por mes con actividad. Un calendario completo ocuparía
// páginas enteras en blanco para decir lo mismo.
function calendarBlock(trainingDates) {
  if (trainingDates.length === 0) return "";

  const byMonth = new Map();
  trainingDates.forEach((iso) => {
    const key = iso.slice(0, 7);
    if (!byMonth.has(key)) byMonth.set(key, new Set());
    byMonth.get(key).add(Number(iso.slice(8, 10)));
  });

  const months = [...byMonth.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, days]) => {
      const [year, month] = key.split("-").map(Number);
      const total = new Date(year, month, 0).getDate();
      const cells = Array.from({ length: total }, (_, i) => {
        const day = i + 1;
        return `<span class="cell${days.has(day) ? " on" : ""}">${day}</span>`;
      }).join("");

      return `<div class="card month">
        <div class="card-head">
          <h4>${MONTHS[month - 1]} ${year}</h4>
          <span class="pill">${days.size} ${days.size === 1 ? "día" : "días"}</span>
        </div>
        <div class="grid">${cells}</div>
      </div>`;
    })
    .join("");

  return `<div class="calendar">${months}</div>`;
}

// ─── Documento ────────────────────────────────────────────────────────────────

const STYLES = `
  :root {
    --paper: #F1EFE8;
    --card: #FBFAF5;
    --ink: #0E1622;
    --dim: #6E7787;
    --line: #D4D2C8;
    --grid: rgba(10, 133, 255, .08);
    --accent: #0A85FF;
    --accent-deep: #0A4E8F;
    --accent-soft: #B9D9FA;
    --up: #1F8F4E;
    --down: #C2412C;
    --mono: ui-monospace, "SF Mono", "Cascadia Mono", "Roboto Mono", Menlo, Consolas,
            "Liberation Mono", "Courier New", monospace;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0; color: var(--ink);
    /* Papel milimetrado: es lo que sostiene a la vez el aire de plano antiguo y
       el de pantalla técnica, sin cargar una sola imagen. */
    background-color: var(--paper);
    background-image:
      linear-gradient(var(--grid) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid) 1px, transparent 1px);
    background-size: 26px 26px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 13.5px; line-height: 1.5; -webkit-font-smoothing: antialiased;
  }
  /* Los titulares van en la condensada que traiga el sistema; si no hay
     ninguna, la sans normal en mayúsculas y apretada sostiene el registro. */
  h1, h2, h3, h4, .kpi-value, .donut-center strong, .step-name {
    font-family: "Archivo Narrow", "Roboto Condensed", "Arial Narrow",
                 "Helvetica Neue Condensed", "Liberation Sans Narrow", sans-serif;
    font-stretch: condensed;
  }
  /* Todo lo que es dato de máquina —etiquetas, cabeceras, fechas, sellos— va en
     monoespaciada: es el detalle que emparenta el listado de ordenador de toda
     la vida con una interfaz de hoy. */
  .eyebrow, .kpi-label, .kpi-index, .tagline, .pill, .tag, th, .strip, footer,
  .note, .legend-value, .donut-center span, .step-detail, .section-num, .reading em {
    font-family: var(--mono);
  }
  .page { max-width: 960px; margin: 0 auto; padding: 30px 26px 70px; }

  /* ── Portada ── */
  header.cover { text-align: center; padding-bottom: 14px; }
  .eyebrow {
    margin: 0; font-size: 9.5px; font-weight: 700; letter-spacing: .24em;
    text-transform: uppercase; color: var(--accent);
  }
  .cover h1 {
    margin: 10px 0 0; font-size: 47px; line-height: .95; letter-spacing: -.01em;
    text-transform: uppercase; font-weight: 800;
    /* Sombra desplazada, como una segunda pasada de tinta mal registrada. */
    text-shadow: 3px 3px 0 var(--accent-soft);
  }
  .cover h1 .hl { color: var(--accent); text-shadow: 3px 3px 0 rgba(14, 22, 34, .13); }
  .tagline {
    margin: 11px 0 0; font-size: 10.5px; font-weight: 600; letter-spacing: .2em;
    text-transform: uppercase; color: var(--dim);
  }
  .tagline u { text-decoration-color: var(--accent); text-underline-offset: 3px; color: var(--ink); }

  /* Franja de metadatos: la cabecera de un listado impreso. */
  .strip {
    display: flex; flex-wrap: wrap; justify-content: center; gap: 0 14px;
    margin-top: 16px; padding: 7px 12px; border: 1px solid var(--line);
    border-left: 3px solid var(--accent); background: var(--card);
    font-size: 9.5px; letter-spacing: .06em; text-transform: uppercase; color: var(--dim);
  }
  .strip b { color: var(--ink); font-weight: 700; }
  .rule-strong {
    height: 3px; margin: 18px 0 20px;
    background: linear-gradient(90deg, var(--accent) 0 22%, var(--ink) 22% 100%);
  }

  /* ── Cifras ── */
  .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
  .kpi {
    position: relative; background: var(--card); border: 1px solid var(--line);
    padding: 15px 12px 12px; text-align: center;
  }
  /* Corchetes de esquina: la escuadra de un plano y el marco de una interfaz
     son el mismo trazo. */
  .kpi::before, .kpi::after {
    content: ""; position: absolute; width: 9px; height: 9px; border: 1.5px solid var(--accent);
  }
  .kpi::before { top: -1px; left: -1px; border-right: 0; border-bottom: 0; }
  .kpi::after { bottom: -1px; right: -1px; border-left: 0; border-top: 0; }
  .kpi-index {
    position: absolute; top: 5px; right: 8px; font-size: 8px; font-weight: 700;
    color: var(--accent); opacity: .5;
  }
  .kpi-icon { color: var(--accent); display: block; margin-bottom: 6px; }
  .icon { display: inline-block; vertical-align: middle; }
  .kpi-value {
    margin: 0; font-size: 31px; line-height: 1; font-weight: 800; letter-spacing: -.02em;
    font-variant-numeric: tabular-nums;
  }
  .kpi-value em { font-style: normal; font-size: 12px; font-weight: 600; color: var(--dim); margin-left: 2px; }
  .kpi-label {
    margin: 7px 0 0; font-size: 8px; font-weight: 700; letter-spacing: .12em;
    text-transform: uppercase; color: var(--dim); line-height: 1.4;
  }
  .kpi-delta { margin: 5px 0 0; font-size: 10px; font-weight: 700; font-family: var(--mono); }
  .kpi-delta em { display: block; font-style: normal; font-weight: 400; font-size: 8px; color: var(--dim); margin-top: 1px; }
  .kpi-hint { margin: 5px 0 0; font-size: 8.5px; color: var(--dim); font-family: var(--mono); }
  .up { color: var(--up); }
  .down { color: var(--down); }
  .flat { color: var(--dim); }

  /* ── Secciones ── */
  section { margin-top: 30px; }
  .section-head {
    display: grid; grid-template-columns: auto auto 1fr; gap: 0 9px; align-items: center;
    border-bottom: 2px solid var(--ink); padding-bottom: 7px; margin-bottom: 14px;
  }
  .section-num {
    font-size: 9.5px; font-weight: 700; color: var(--accent);
    border: 1px solid var(--accent); padding: 1px 5px;
  }
  .section-icon { color: var(--accent); display: flex; }
  .section-head h2 {
    margin: 0; font-size: 19px; text-transform: uppercase; letter-spacing: .02em; font-weight: 800;
  }
  .section-head p { grid-column: 3; margin: 1px 0 0; font-size: 10.5px; color: var(--dim); }

  .card { background: var(--card); border: 1px solid var(--line); padding: 13px 15px; }
  /* Los hijos de un grid nacen con min-width:auto, así que crecen hasta lo que
     mida su contenido y se llevan por delante el ancho del track: una tabla que
     no cabe estiraba la tarjeta y, con ella, la página entera. Con esto el
     desbordamiento se queda dentro de la caja que sí sabe desplazarse. */
  .kpis > *, .stack > *, .split > *, .metrics > *, .calendar > *, .donut > *, .diet > * { min-width: 0; }
  .stack { display: grid; gap: 12px; }
  .card-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
  .card-head h3 { margin: 0; font-size: 15px; text-transform: uppercase; letter-spacing: .01em; }
  .card-head h4 { margin: 0; font-size: 13px; text-transform: uppercase; }
  .pill {
    font-size: 9px; font-weight: 600; color: var(--dim); background: var(--paper);
    border: 1px solid var(--line); padding: 2px 8px; white-space: nowrap;
    text-transform: uppercase; letter-spacing: .05em;
  }
  .tag {
    font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em;
    color: #fff; background: var(--accent); padding: 2px 7px; vertical-align: 2px;
  }

  /* ── Recorrido de la rutina ── */
  .flow { display: flex; align-items: stretch; justify-content: center; gap: 6px; flex-wrap: wrap; }
  .step {
    background: var(--card); border: 1px solid var(--line);
    padding: 12px 14px 10px; text-align: center; min-width: 106px; position: relative;
  }
  .step::before {
    content: ""; position: absolute; top: -1px; left: -1px; right: -1px; height: 2px; background: var(--accent);
  }
  .step-index {
    position: absolute; top: -9px; left: 50%; transform: translateX(-50%);
    width: 18px; height: 18px; background: var(--accent); color: #fff;
    font-size: 9.5px; font-weight: 700; line-height: 18px; font-family: var(--mono);
  }
  .step-name { margin: 6px 0 0; font-size: 14px; font-weight: 800; text-transform: uppercase; }
  .step-detail { margin: 3px 0 0; font-size: 8.5px; color: var(--dim); }
  .step-arrow { align-self: center; color: var(--accent); font-size: 17px; font-weight: 700; }
  .step-loop { border-style: dashed; }
  .step-loop::before { background: repeating-linear-gradient(90deg, var(--accent) 0 5px, transparent 5px 9px); }

  /* ── Barras y donut ── */
  .split { display: grid; grid-template-columns: 1.25fr 1fr; gap: 12px; align-items: start; }
  .bars { display: grid; gap: 7px; }
  .bar-row { display: grid; grid-template-columns: 96px 1fr 34px; align-items: center; gap: 9px; }
  .bar-label { font-size: 11.5px; font-weight: 600; }
  /* Barras en segmentos, como un indicador de diodos: la misma lectura que una
     barra maciza, con el pulso de un panel. */
  .bar-track {
    height: 10px; background: repeating-linear-gradient(90deg, #DFE3E8 0 5px, transparent 5px 8px);
    overflow: hidden;
  }
  .bar-fill {
    display: block; height: 100%;
    background: repeating-linear-gradient(90deg, var(--accent) 0 5px, transparent 5px 8px);
  }
  .bar-value {
    font-size: 10.5px; text-align: right; font-variant-numeric: tabular-nums;
    color: var(--dim); font-weight: 600; font-family: var(--mono);
  }

  .donut { display: flex; align-items: center; gap: 14px; }
  .donut-side { flex: 1; min-width: 0; }
  .note { margin: 8px 0 0; font-size: 9.5px; line-height: 1.4; color: var(--dim); }
  .donut-chart { position: relative; flex: none; }
  .donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .donut-center strong { font-size: 25px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
  .donut-center span { font-size: 7.5px; font-weight: 700; letter-spacing: .16em; color: var(--dim); margin-top: 3px; }
  .legend { list-style: none; margin: 0; padding: 0; display: grid; gap: 5px; flex: 1; min-width: 0; }
  .legend li { display: grid; grid-template-columns: 9px 1fr auto; align-items: center; gap: 7px; font-size: 11px; }
  .dot { width: 9px; height: 9px; }
  .legend-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .legend-value { font-variant-numeric: tabular-nums; font-weight: 700; }

  /* ── Tablas ── */
  table { width: 100%; border-collapse: collapse; }
  th {
    text-align: left; font-size: 8.5px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: var(--dim); padding: 0 7px 6px; border-bottom: 1px solid var(--line);
  }
  td { padding: 7px; border-bottom: 1px solid #F0E9DA; vertical-align: middle; }
  tr:last-child td { border-bottom: 0; }
  .right { text-align: right; }
  .nums { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .dim { color: var(--dim); }
  .strong { font-weight: 700; }
  .empty { color: var(--dim); font-size: 12px; margin: 6px 0 0; }
  .ex-name { font-weight: 600; }
  .ex-group { display: block; font-size: 10px; color: var(--dim); }
  .spark-cell { width: 66px; color: var(--accent); line-height: 0; }
  .spark { display: block; }

  /* ── Ciclos ── */
  .cycle-stats {
    display: flex; flex-wrap: wrap; gap: 16px; font-size: 11.5px; color: var(--dim);
    border-bottom: 1px solid var(--line); padding-bottom: 9px; margin-bottom: 4px;
  }
  .cycle-stats strong { color: var(--accent); font-size: 15px; font-variant-numeric: tabular-nums; }
  .cycle-day { padding-top: 6px; }
  .cycle-day-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
  .cycle-day-head h4 {
    margin: 0; font-size: 12px; text-transform: uppercase; color: var(--accent);
    border-left: 3px solid var(--accent); padding-left: 6px;
  }
  .sets { width: 54%; }
  .set {
    display: inline-block; font-size: 10.5px; font-variant-numeric: tabular-nums;
    font-family: var(--mono); background: var(--paper); border: 1px solid var(--line);
    padding: 1px 5px; margin: 0 3px 3px 0;
  }
  .set em { font-style: normal; color: var(--dim); font-size: 9px; }

  /* ── Medidas y calendario ── */
  .metrics { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 14px; }
  .metric h4 { margin: 0 0 5px; font-size: 11.5px; text-transform: uppercase; }
  .readings { display: flex; flex-wrap: wrap; gap: 4px; }
  .reading {
    font-size: 10px; font-variant-numeric: tabular-nums; background: var(--card);
    border: 1px solid var(--line); padding: 1px 5px;
  }
  .reading em { font-style: normal; color: var(--accent); margin-right: 4px; }

  .diet { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .diet-day .card-head { margin-bottom: 5px; }
  .diet-day .card-head h4 { color: var(--accent); }
  .meal-name {
    width: 74px; font-family: var(--mono); font-size: 8.5px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; color: var(--dim);
    text-align: left; vertical-align: top; padding: 6px 7px; border-bottom: 1px solid #ECEAE2;
  }
  .diet-note {
    margin: 12px 0 0; padding: 8px 12px; border-left: 3px solid var(--accent);
    background: var(--card); border-top: 1px solid var(--line); border-right: 1px solid var(--line);
    border-bottom: 1px solid var(--line); font-size: 11px; color: var(--ink);
  }

  .calendar { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .grid { display: grid; grid-template-columns: repeat(11, 1fr); gap: 3px; }
  .cell {
    font-size: 8.5px; text-align: center; padding: 3px 0; font-family: var(--mono);
    background: var(--paper); border: 1px solid var(--line); color: #B4B8BF; font-variant-numeric: tabular-nums;
  }
  /* El halo se pierde al imprimir, y ahí sobra: en pantalla es un piloto
     encendido, en papel basta el bloque azul. */
  .cell.on {
    background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 700;
    box-shadow: 0 0 6px rgba(10, 133, 255, .45);
  }

  footer {
    margin-top: 34px; padding-top: 13px; border-top: 2px solid var(--ink);
    display: flex; justify-content: space-between; gap: 14px; flex-wrap: wrap;
    font-size: 9px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: var(--dim);
  }
  footer .sig { color: var(--accent); }

  .print-bar { position: fixed; right: 18px; bottom: 18px; }
  .print-bar button {
    font-family: var(--mono); font-weight: 700; font-size: 11px; letter-spacing: .1em; text-transform: uppercase;
    color: #fff; background: var(--accent); border: 0; padding: 13px 22px;
    cursor: pointer; box-shadow: 0 0 0 1px var(--accent), 4px 4px 0 rgba(10, 78, 143, .35);
  }

  @media (max-width: 760px) {
    .page { padding: 22px 14px 62px; }
    .cover h1 { font-size: 30px; }
    .kpis { grid-template-columns: repeat(2, 1fr); }
    /* Con las cifras en dos columnas, la última de un número impar se quedaba
       a media fila. Que ocupe el ancho entero. */
    .kpis > *:last-child:nth-child(odd) { grid-column: span 2; }
    .split, .metrics, .calendar { grid-template-columns: 1fr; }
    .bar-row { grid-template-columns: 84px 1fr 30px; }
    .donut { flex-direction: column; align-items: flex-start; }
    /* Toda tabla se desplaza dentro de su tarjeta en vez de estirar la página:
       basta con que una columna no quepa para que el documento entero coja
       scroll horizontal. Como el arrastre ya resuelve el ancho, las celdas no
       se parten —una columna de tres renglones se lee peor—, salvo las series
       de un ciclo, que son etiquetas sueltas y quedan mejor envueltas. */
    .card table { display: block; overflow-x: auto; }
    .card th, .card td { white-space: nowrap; }
    .card td.sets { white-space: normal; min-width: 118px; }
  }

  @page { margin: 12mm; }
  @media print {
    .print-bar { display: none; }
    .page { padding: 0; max-width: none; }
    body { font-size: 10.5px; background-size: 22px 22px; }
    /* El halo de los días entrenados es un piloto de pantalla; impreso solo
       ensucia el borde del bloque. */
    .cell.on { box-shadow: none; }
    .print-bar button { box-shadow: none; }
    /* Sin esto el navegador imprime en blanco las barras, el donut y el
       calendario, que es justo la información que aportan. */
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    section, .card, .metric, .month, .donut { break-inside: avoid; page-break-inside: avoid; }
    h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
  }
`;

export function buildHtmlReport(model) {
  const { meta, summary } = model;
  const period =
    summary.firstTrainingDate && summary.lastTrainingDate
      ? `${date(summary.firstTrainingDate)} — ${date(summary.lastTrainingDate)}`
      : "Sin entrenamientos registrados";

  const kpis = [
    { name: "sets", value: summary.totalSets, label: "Series completadas", delta: summary.lastCycle?.sets },
    { name: "volume", value: summary.totalVolume, unit: "kg", label: "Volumen levantado", delta: summary.lastCycle?.volume },
    { name: "days", value: summary.trainingDays, label: "Días entrenados", hint: `Racha de ${summary.currentStreak}` },
    { name: "cycles", value: summary.cyclesCompleted, label: "Ciclos cerrados", hint: `Semana ${summary.cycle} en curso` },
    { name: "streak", value: summary.bestStreak, unit: "días", label: "Mejor racha", hint: `Objetivo ${summary.weeklyGoal} series` },
  ]
    .map((data, i) => kpi(i + 1, data))
    .join("");

  // El ciclo en curso encabeza la lista y los archivados van detrás, del más
  // reciente al más antiguo.
  const cycles = [
    model.currentCycle.days.length > 0
      ? cycleBlock({ ...model.currentCycle, week: model.routine.week }, { open: true })
      : "",
    ...model.weeks.map((week) => cycleBlock(week)),
  ]
    .filter(Boolean)
    .join("");

  const bars = muscleBars(model.volumeByGroup);
  const reparto = bars
    ? `<div class="split">
        <div class="card">${bars}</div>
        <div class="card">${donut(model.volumeByGroup, summary.totalSets)}</div>
      </div>`
    : "";

  // Se numeran solo las secciones que traen datos: un informe con saltos en la
  // numeración parece incompleto aunque no falte nada.
  const sections = [
    ["recorrido", "routine", "Tu ciclo de entrenamiento", "Una vuelta completa a la rutina", flow(model.routine.days)],
    ["musculos", "muscle", "Volumen por músculo", "Series completadas en todo el historial", reparto],
    ["progresion", "exercises", "Progresión por ejercicio", "Del primer peso registrado al último", exerciseTable(model.exercises)],
    ["rutina", "goal", "Rutina actual", `Semana ${model.routine.week}`, routineBlock(model.routine)],
    ["ciclos", "cycleList", "Historial de ciclos", "Lo levantado en cada serie, ciclo a ciclo", cycles],
    ["dieta", "diet", "Dieta semanal", "Lo que toca cada día", dietBlock(model.diet)],
    ["medidas", "measures", "Medidas corporales", "Peso, composición y perímetros", measurementsBlock(model.measurements)],
    ["calendario", "calendar", "Días entrenados", `${num(summary.trainingDays)} sesiones registradas`, calendarBlock(model.trainingDates)],
  ]
    .filter((entry) => entry[4])
    .map((entry, i) => section(i + 1, ...entry))
    .join("");

  const strip = [
    ["Atleta", meta.userName || "Sin nombre"],
    ["Periodo", period],
    ["Ciclos", `${summary.cyclesCompleted} cerrados`],
    ["Series", num(summary.totalSets)],
    ["Emitido", date(meta.generatedAt, { day: "2-digit", month: "2-digit", year: "numeric" })],
  ]
    .map(([label, value]) => `<span>${esc(label)}: <b>${esc(value)}</b></span>`)
    .join("");

  const body = `
  <div class="page">
    <header class="cover">
      <p class="eyebrow">TRYAD // Informe de entrenamiento</p>
      <h1>${meta.userName ? `El entrenamiento de <span class="hl">${esc(meta.userName)}</span>` : 'Tu <span class="hl">entrenamiento</span>'}</h1>
      <p class="tagline">Progresando <u>serie a serie</u>.</p>
      <div class="strip">${strip}</div>
    </header>

    <div class="rule-strong"></div>

    <div class="kpis">${kpis}</div>

    ${sections}

    <footer>
      <span class="sig">TRYAD</span>
      <span>${esc(meta.email || "Informe personal")}</span>
      <span>Generado el ${date(meta.generatedAt)}</span>
      <span>Funciona sin conexión</span>
    </footer>
  </div>

  <div class="print-bar">
    <button onclick="window.print()">Imprimir · Guardar PDF</button>
  </div>`;

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>TRYAD · Informe de ${esc(meta.userName || "entrenamiento")}</title>
<style>${STYLES}</style>
</head>
<body>${body}</body>
</html>`;
}
